import { useEffect, useState } from "react";
import { api, useAuth } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ClosingChecklist() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [newSectionName, setNewSectionName] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newItemSectionId, setNewItemSectionId] = useState("");
  const [checkedState, setCheckedState] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sectionsRes, itemsRes] = await Promise.all([
        api.get("/closing-checklist/sections"),
        api.get("/closing-checklist/items"),
      ]);
      setSections(sectionsRes.data || []);
      setItems(itemsRes.data || []);
      const initialExpanded = {};
      (sectionsRes.data || []).forEach((s) => { initialExpanded[s.section_id] = true; });
      setExpandedSections(initialExpanded);
      const initialChecked = {};
      (itemsRes.data || []).forEach((it) => { initialChecked[it.item_id] = true; });
      setCheckedState(initialChecked);
    } catch (error) {
      toast.error("Failed to load closing checklist");
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleToggleCheckbox = (itemId, checked) => {
    setCheckedState((prev) => ({ ...prev, [itemId]: checked }));
  };

  const handleAddSection = async () => {
    if (!isAdmin) return;
    const name = newSectionName.trim();
    if (!name) { toast.error(t("closing.addSectionPlaceholder")); return; }
    try {
      const res = await api.post("/closing-checklist/sections", { name });
      setSections((prev) => [...prev, res.data]);
      setNewSectionName("");
      setExpandedSections((prev) => ({ ...prev, [res.data.section_id]: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleAddItem = async () => {
    if (!isAdmin) return;
    const name = newItemName.trim();
    if (!name) { toast.error(t("closing.addItemPlaceholder")); return; }
    try {
      const res = await api.post("/closing-checklist/items", {
        name,
        section_id: newItemSectionId || null,
      });
      setItems((prev) => [...prev, res.data]);
      setNewItemName("");
      setCheckedState((prev) => ({ ...prev, [res.data.item_id]: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!isAdmin) return;
    const section = sections.find((s) => s.section_id === sectionId);
    if (!section) return;
    const ok = window.confirm(`${t("closing.delete")} "${section.name}"?`);
    if (!ok) return;
    try {
      await api.delete(`/closing-checklist/sections/${sectionId}`);
      setItems((prev) => prev.map((it) => (it.section_id === sectionId ? { ...it, section_id: null } : it)));
      setSections((prev) => prev.filter((s) => s.section_id !== sectionId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleEditItemName = async (itemId, newName) => {
    if (!isAdmin) return;
    try {
      const res = await api.put(`/closing-checklist/items/${itemId}`, { name: newName });
      setItems((prev) => prev.map((it) => (it.item_id === itemId ? res.data : it)));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!isAdmin) return;
    try {
      await api.delete(`/closing-checklist/items/${itemId}`);
      setItems((prev) => prev.filter((it) => it.item_id !== itemId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const itemsBySection = (sectionId) =>
    items.filter((it) => (sectionId ? it.section_id === sectionId : !it.section_id));

  const handleSubmitClosing = async () => {
    const insufficientItemIds = items
      .filter((item) => checkedState[item.item_id] === false)
      .map((item) => item.item_id);
    setSubmitting(true);
    try {
      await api.post("/closing-checklist/submit", {
        date: new Date().toISOString().split("T")[0],
        insufficient_item_ids: insufficientItemIds,
      });
      toast.success(lang === "fr" ? "Clôture enregistrée" : "Cierre guardado correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === "fr" ? "Erreur lors de la clôture" : "Error al guardar el cierre"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="closing-checklist-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-foreground">{t("closing.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("closing.subtitle")}</p>
      </div>

      {isAdmin && <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">{t("closing.sectionsTitle")}</CardTitle>
          <CardDescription>{t("closing.sectionsDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder={t("closing.addSectionPlaceholder")}
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              data-testid="new-section-input"
            />
            <Button onClick={handleAddSection} data-testid="add-section-btn">
              <Plus className="h-4 w-4 mr-1" />
              {t("closing.addSection")}
            </Button>
          </div>
          <div className="space-y-2 text-sm text-muted-foreground">
            {sections.length === 0 && <p>{t("closing.noSections")}</p>}
            {sections.map((section) => (
              <div key={section.section_id} className="flex items-center justify-between rounded border border-border px-3 py-2 bg-zinc-50">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => toggleSection(section.section_id)}>
                    {expandedSections[section.section_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </Button>
                  <span className="font-medium">{section.name}</span>
                </div>
                <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handleDeleteSection(section.section_id)} data-testid={`delete-section-${section.section_id}`}>
                  {t("closing.delete")}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>}

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">{t("closing.itemsTitle")}</CardTitle>
          <CardDescription>{t("closing.itemsDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isAdmin && <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <Input
              placeholder={t("closing.addItemPlaceholder")}
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              data-testid="new-item-input"
            />
            <Select
              value={newItemSectionId || "__none__"}
              onValueChange={(val) => setNewItemSectionId(val === "__none__" ? "" : val)}
            >
              <SelectTrigger className="md:w-48" data-testid="item-section-select">
                <SelectValue placeholder={t("closing.noSection")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("closing.noSection")}</SelectItem>
                {sections.map((s) => (
                  <SelectItem key={s.section_id} value={s.section_id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleAddItem} data-testid="add-item-btn">
              <Plus className="h-4 w-4 mr-1" />
              {t("closing.addItem")}
            </Button>
          </div>}

          <div className="space-y-4">
            {sections.map((section) => {
              const sectionItems = itemsBySection(section.section_id);
              if (!sectionItems.length) return null;
              const expanded = expandedSections[section.section_id];
              return (
                <div key={section.section_id} className="border border-border rounded-lg">
                  <button type="button" className="w-full flex items-center justify-between px-4 py-2 bg-zinc-50" onClick={() => toggleSection(section.section_id)}>
                    <span className="font-medium text-foreground">{section.name}</span>
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {expanded && (
                    <div className="divide-y divide-zinc-100">
                      {sectionItems.map((item) => (
                        <ChecklistRow key={item.item_id} item={item} checked={checkedState[item.item_id] ?? true} onToggle={handleToggleCheckbox} onEditName={handleEditItemName} onDelete={handleDeleteItem} isAdmin={isAdmin} t={t} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {itemsBySection(null).length > 0 && (
              <div className="border border-border rounded-lg">
                <div className="px-4 py-2 bg-zinc-50">
                  <span className="font-medium text-foreground">{t("closing.unassigned")}</span>
                </div>
                <div className="divide-y divide-zinc-100">
                  {itemsBySection(null).map((item) => (
                    <ChecklistRow key={item.item_id} item={item} checked={checkedState[item.item_id] ?? true} onToggle={handleToggleCheckbox} onEditName={handleEditItemName} onDelete={handleDeleteItem} isAdmin={isAdmin} t={t} />
                  ))}
                </div>
              </div>
            )}

            {items.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("closing.noItems")}</p>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={handleSubmitClosing} disabled={submitting || items.length === 0} data-testid="submit-closing-btn">
              {submitting
                ? (lang === "fr" ? "Enregistrement..." : "Guardando...")
                : (lang === "fr" ? "Valider la clôture" : "Completar cierre")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ChecklistRow({ item, checked, onToggle, onEditName, onDelete, isAdmin, t }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);

  const handleBlur = async () => {
    if (name.trim() && name.trim() !== item.name) {
      await onEditName(item.item_id, name.trim());
    } else {
      setName(item.name);
    }
    setEditing(false);
  };

  return (
    <div className="flex items-center justify-between px-4 py-2">
      <div className="flex items-center gap-3">
        <Checkbox checked={checked} onCheckedChange={(val) => onToggle(item.item_id, Boolean(val))} data-testid={`closing-checkbox-${item.item_id}`} />
        <div>
          <Label className="text-sm font-medium">{t("closing.sufficientStock")}</Label>
          {isAdmin && editing ? (
            <Input className="mt-1 h-7 text-sm" value={name} onChange={(e) => setName(e.target.value)} onBlur={handleBlur} autoFocus />
          ) : (
            <p className={`text-sm text-foreground mt-1 ${isAdmin ? "cursor-pointer hover:underline" : ""}`} onClick={() => { if (isAdmin) setEditing(true); }}>{item.name}</p>
          )}
        </div>
      </div>
      {isAdmin && (
        <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => onDelete(item.item_id)}>
          {t("closing.remove")}
        </Button>
      )}
    </div>
  );
}
